import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  memo,
  createContext,
  useContext,
} from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Paper,
  InputAdornment,
  createFilterOptions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { SelectedItem } from "./SelectedItem";

const defaultFilterOptions = createFilterOptions();

const MenuSearchContext = createContext({
  menuSearch: "",
  setMenuSearch: () => {},
  searchInputRef: { current: null },
  markIgnoreClose: () => {},
});

function keepPopupOpenOnMouseDown(e) {
  if (e.target.closest("[data-menu-search]")) return;
  e.preventDefault();
}

/** Stable Paper identity so the popup does not remount while typing. */
function AutocompleteSearchPaper({ children, ...paperProps }) {
  const {
    menuSearch,
    setMenuSearch,
    searchInputRef,
    markIgnoreClose,
    closeMenu,
  } = useContext(MenuSearchContext);

  return (
    <Paper {...paperProps} onMouseDown={keepPopupOpenOnMouseDown}>
      <Box
        data-menu-search
        sx={{
          px: 1.5,
          pt: 1.5,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
        }}
        onMouseDown={(e) => {
          markIgnoreClose();
          e.stopPropagation();
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search..."
          value={menuSearch}
          inputRef={searchInputRef}
          onChange={(e) => setMenuSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              closeMenu();
              return;
            }
            // Stop other keys from reaching Autocomplete's own key handler
            // (prevents arrow-key conflicts, Home/End, etc.)
            e.stopPropagation();
          }}
          onMouseDown={() => markIgnoreClose()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {children}
    </Paper>
  );
}

function CustomAutocomplete({
  name,
  size = "small",
  label,
  value,
  options,
  handleChangeAdvance,
  disabled = false,
  required = false,
  loading = false,
  getOptionDisabled,
  helperText = "",
  getOptionLabel,
  checks = [],
  errors = [],
  filterOptions,
  renderInput,
  ListboxComponent,
  onBlur,
  renderOption,
  highlightError = false,
  selectedItemConfig = { mainTitleKey: "label" },
  selectedItemVariant,
  hasVisiblePills = false,
}) {
  const [error, setError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState("");
  const searchInputRef = useRef(null);
  const ignoreCloseRef = useRef(false);
  const rootRef = useRef(null);

  const safeOptions = Array.isArray(options) ? options : [];
  const canClear = !disabled && !required;
  const resolveOptionLabel = useMemo(
    () => getOptionLabel || ((op) => op?.label || ""),
    [getOptionLabel]
  );

  const markIgnoreClose = useCallback(() => {
    ignoreCloseRef.current = true;
  }, []);

  const focusTrigger = useCallback(() => {
    // Return focus to the Autocomplete's native input after menu closes.
    // setTimeout(0) lets MUI finish its own cleanup first.
    setTimeout(() => {
      rootRef.current?.querySelector("input")?.focus();
    }, 0);
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setMenuSearch("");
    ignoreCloseRef.current = false;
    focusTrigger();
  }, [focusTrigger]);

  const menuSearchContextValue = useMemo(
    () => ({
      menuSearch,
      setMenuSearch,
      searchInputRef,
      markIgnoreClose,
      closeMenu,
    }),
    [menuSearch, markIgnoreClose, closeMenu]
  );

  const handleError = () => {
    let flag = false;
    for (let i = 0; i < checks.length; i++) {
      if (!checks[i]) {
        flag = true;
        setError(true);
        setShowError(true);
        setIndex(i);
        break;
      }
    }
    if (!flag) {
      setError(false);
      setShowError(false);
    }
  };

  useEffect(() => {
    handleError();
  }, [checks]);

  useEffect(() => {
    if (highlightError) handleError();
  }, [highlightError]);

  useEffect(() => {
    if (!open) {
      setMenuSearch("");
      return;
    }
    markIgnoreClose();
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open, markIgnoreClose]);

  // Autocomplete blur-close does not run while focus is in the menu search;
  // close explicitly when clicking outside the field + popup.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (rootRef.current?.contains(target)) return;
      if (target.closest("[data-autocomplete-popup]")) return;
      closeMenu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, closeMenu]);

  const handleBlur = () => {
    if (error || (required && (value === null || value === ""))) {
      setShowError(true);
    } else {
      setShowError(false);
    }
    onBlur?.();
  };

  const selectedOption =
    safeOptions.find((op) => op.value === value) || null;
  const isError =
    (showError && !!errors[index]) ||
    (showError && required && !selectedOption);
  const errorMsg = isError
    ? errors[index] || "This field is required"
    : helperText || " ";

  const applyFilter = useCallback(
    (opts, search) => {
      const state = {
        inputValue: search || "",
        getOptionLabel: resolveOptionLabel,
      };
      if (filterOptions) {
        return filterOptions(opts, state);
      }
      return defaultFilterOptions(opts, state);
    },
    [filterOptions, resolveOptionLabel]
  );

  const defaultRenderInput = (params) => {
    if (!selectedOption) {
      return (
        <TextField
          {...params}
          label={label}
          required={required}
          error={isError}
          helperText={errorMsg}
          inputProps={{
            ...params.inputProps,
            readOnly: true,
            onFocus: (e) => {
              if (open) {
                searchInputRef.current?.focus();
                return;
              }
              params.inputProps.onFocus?.(e);
            },
            onBlur: (e) => {
              if (open && e.relatedTarget?.closest("[data-menu-search]")) return;
              params.inputProps.onBlur?.(e);
            },
          }}
        />
      );
    }

    const { endAdornment, ...restOfInputProps } = params.InputProps;

    return (
      <TextField
        {...params}
        label={label}
        required={required}
        error={isError}
        helperText={errorMsg}
        slotProps={{ inputLabel: { shrink: true } }}
        inputProps={{
          ...params.inputProps,
          readOnly: true,
          onFocus: (e) => {
            if (open) {
              searchInputRef.current?.focus();
              return;
            }
            params.inputProps.onFocus?.(e);
          },
          onBlur: (e) => {
            if (open && e.relatedTarget?.closest("[data-menu-search]")) return;
            params.inputProps.onBlur?.(e);
          },
          style: {
            opacity: 0,
            width: 1,
            minWidth: 1,
            padding: 0,
            border: "none",
          },
        }}
        InputProps={{
          ...restOfInputProps,
          startAdornment: (
            <SelectedItem
              option={selectedOption}
              config={selectedItemConfig}
              variant={selectedItemVariant}
              hasVisiblePills={hasVisiblePills}
              showClear
              onDelete={() => {
                if (canClear) {
                  handleChangeAdvance(name, null);
                }
              }}
              disabled={disabled || !canClear}
              onClick={() => {
                if (!disabled) setOpen(true);
              }}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setOpen(true);
                }
                if (e.key === "Delete" || e.key === "Backspace") {
                  if (canClear) {
                    e.preventDefault();
                    handleChangeAdvance(name, null);
                  }
                }
              }}
              endAdornment={endAdornment}
            />
          ),
        }}
      />
    );
  };

  const defaultRenderOption = (props, option) => {
    const { key, ...optionProps } = props;
    return (
      <Box key={option.value || key} component="li" {...optionProps}>
        {resolveOptionLabel(option)}
      </Box>
    );
  };

  return (
    <MenuSearchContext.Provider value={menuSearchContextValue}>
      <Box ref={rootRef} sx={{ width: "100%" }}>
        <Autocomplete
          size={size}
          open={open}
          onOpen={() => {
            if (!disabled) setOpen(true);
          }}
          onClose={(event, reason) => {
            if (reason === "blur" && ignoreCloseRef.current) {
              ignoreCloseRef.current = false;
              return;
            }
            closeMenu();
          }}
          disabled={disabled}
          options={safeOptions}
          value={selectedOption}
          inputValue=""
          onInputChange={() => {}}
          loading={loading}
          disableClearable
          disableListWrap
          getOptionDisabled={getOptionDisabled}
          getOptionLabel={resolveOptionLabel}
          isOptionEqualToValue={(option, val) => option?.value === val?.value}
          filterOptions={(opts) => applyFilter(opts, menuSearch)}
          onChange={(e, val) => {
            if (disabled) return;
            if (val === null) {
              if (!canClear) return;
              handleChangeAdvance(name, null);
              setShowError(false);
              closeMenu();
              return;
            }
            handleChangeAdvance(name, val.value, val.label);
            setShowError(false);
            closeMenu();
          }}
          onBlur={handleBlur}
          ListboxComponent={ListboxComponent}
          PaperComponent={AutocompleteSearchPaper}
          renderOption={renderOption || defaultRenderOption}
          renderInput={renderInput || defaultRenderInput}
          slotProps={{
            popper: {
              onMouseDown: keepPopupOpenOnMouseDown,
              "data-autocomplete-popup": true,
            },
          }}
        />
      </Box>
    </MenuSearchContext.Provider>
  );
}

export default memo(CustomAutocomplete);
